import { createSlice } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import toastWrapper from "../../components/toastWrapper";
import { entrantFormService } from "../../services/entrantService";
import { getProfileInfo } from "../userSlice";
import { createCustomAsyncThunk, defaultEntityAdapter } from "../utils";

export const getEnrollConsent = createCustomAsyncThunk(
    "entrantForm/enrollConsent/get",
    async userId => await entrantFormService.getEnrollConsent(userId)
)

export const createEnrollConsent = createCustomAsyncThunk(
    "entrantForm/enrollConsent/create",
    async data => await entrantFormService.postEnrollConsent(data)
)

const saveTempFiles = createCustomAsyncThunk(
    "entrantForm/supDocs/tempFiles/post",
    async files => await Promise.all(
        files.map(file => entrantFormService.postTempFile(file).then(res => ({ ...res, name: file.name })))
    ).then(res => ({ data: res }))
)

const enrollConsentSlice = createSlice({
    name: "entrantForm/enrollConsent",
    initialState: defaultEntityAdapter.getInitialState({
        isLoading: true,
        error: null,
        acceptedFiles: [],
        tempFiles: [],
    }),
    reducers: {
        setAcceptedFiles: (state, action) => { state.acceptedFiles = action.payload },
        resetAcceptedFiles: state => { state.acceptedFiles = [] },
        deleteAcceptedFile: (state, action) => {
            // payload - индекс 
            // Этот кошмар нужен, чтобы после удаления не оставались tempFiles, которые пользователь удалил
            let fileToDelete = state.acceptedFiles[action.payload]
            state.tempFiles = state.tempFiles.filter(tmp => tmp.name !== fileToDelete.name)
            state.acceptedFiles = state.acceptedFiles.filter((_, acc) => acc !== action.payload)
        },
        resetTempFiles: state => { state.tempFiles = [] }
    },
    extraReducers: builder => {
        builder
            .addCase(getEnrollConsent.pending, state => { state.isLoading = true })
            .addCase(getEnrollConsent.fulfilled, (state, action) => {
                state.isLoading = false
                defaultEntityAdapter.setAll(state, action.payload)
            })
            .addCase(getEnrollConsent.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.error
            })

            .addCase(createEnrollConsent.pending, state => { state.isLoading = true })
            .addCase(createEnrollConsent.fulfilled, (state, action) => {
                state.isLoading = false
                defaultEntityAdapter.upsertOne(state, action.payload)
            })
            .addCase(createEnrollConsent.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.error
            })

            .addCase(saveTempFiles.pending, state => { state.tempFilesIsLoading = true })
            .addCase(saveTempFiles.fulfilled, (state, action) => {
                state.tempFiles = [...state.tempFiles, ...action.payload]
                state.tempFilesIsLoading = false
            })
            .addCase(saveTempFiles.rejected, (state, action) => {
                state.error = action.error
                state.tempFilesIsLoading = false
            })
    }
})

const enrollConsentSelectors = defaultEntityAdapter.getSelectors(x => x.entrantForm.enrollConsent)

export default enrollConsentSlice.reducer

export const useEnrollConsents = userId => {

    const dispatch = useDispatch()

    useEffect(() => {
        if (userId) dispatch(getEnrollConsent(userId))
    }, [dispatch, userId])

    const { isLoading, acceptedFiles, tempFiles } = useSelector(x => x.entrantForm.enrollConsent)

    const consents = useSelector(enrollConsentSelectors.selectAll)

    const latestConsent = consents.length === 0 ? null : consents.reduce(
        (latestItem, currentItem) => new Date(currentItem.action_date) > new Date(latestItem.action_date) ? currentItem : latestItem
    )

    const isConsentNotSubmitted = !latestConsent || latestConsent?.type === "not_submitted"

    const handleAddConsent = type => dispatch(createEnrollConsent({
        user: userId,
        type,
        files: tempFiles.map(x => x.id)
    })).unwrap()
        .then(() => {
            toastWrapper("Изменения сохранены", "success")
            dispatch(getProfileInfo(userId))
            dispatch(enrollConsentSlice.actions.resetAcceptedFiles())
            dispatch(enrollConsentSlice.actions.resetTempFiles())
        })
        .catch(e => {
            dispatch(getProfileInfo(userId))
            console.error(e)
            toastWrapper(e?.getUserMessage ? e.getUserMessage() : "Не удалось сохранить изменения")
        })

    const handleDrop = files => {
        dispatch(saveTempFiles(files))
        dispatch(enrollConsentSlice.actions.setAcceptedFiles(files))
    }

    const handleDeleteAcceptedFile = index => dispatch(enrollConsentSlice.actions.deleteAcceptedFile(index))

    return {
        isLoading, consents, latestConsent, isConsentNotSubmitted,
        handleAddConsent, canAddConsent: tempFiles?.length > 0,
        acceptedFiles, handleDrop, handleDeleteAcceptedFile,
    }
}